// src/components/TimeFieldConfig.jsx
import React from 'react';
import {
    Box,
    Typography,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Chip,
    TextField,
    Button,
    IconButton,
    Paper,
    Divider,
    alpha
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PublicIcon from '@mui/icons-material/Public';
import { TIMEZONES } from '../types/schema';

const TimeFieldConfig = ({ field, onChange }) => {
    const handleTimeConfigChange = (property) => (event) => {
        const value = event.target.type === 'checkbox'
            ? event.target.checked
            : event.target.value;

        onChange({
            ...field,
            timeConfig: {
                ...field.timeConfig,
                [property]: value
            }
        });
    };

    const handleTimezoneUpdate = (index, value) => {
        const newTimezones = [...field.timeConfig.availableTimezones];
        newTimezones[index] = value;
        onChange({
            ...field,
            timeConfig: {
                ...field.timeConfig,
                availableTimezones: newTimezones
            }
        });
    };

    const handleAddTimezone = () => {
        onChange({
            ...field,
            timeConfig: {
                ...field.timeConfig,
                availableTimezones: [...field.timeConfig.availableTimezones, 'New Timezone']
            }
        });
    };

    const handleDeleteTimezone = (index) => {
        const newTimezones = field.timeConfig.availableTimezones.filter((_, i) => i !== index);
        onChange({
            ...field,
            timeConfig: {
                ...field.timeConfig,
                availableTimezones: newTimezones
            }
        });
    };

    const handleDefaultTimezoneChange = (event) => {
        onChange({
            ...field,
            validation: {
                ...field.validation,
                timezone: event.target.value
            }
        });
    };

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                mt: 2,
                bgcolor: alpha('#0288d1', 0.05),
                border: 1,
                borderColor: 'primary.light',
                borderRadius: 2
            }}
        >
            <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AccessTimeIcon color="primary" />
                <Typography variant="h6" fontWeight={600}>
                    Time Field Configuration
                </Typography>
            </Box>

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={field.timeConfig?.includeTimezone || true}
                                onChange={handleTimeConfigChange('includeTimezone')}
                                color="primary"
                            />
                        }
                        label={
                            <Box display="flex" alignItems="center" gap={1}>
                                <PublicIcon fontSize="small" />
                                Include Timezone Selection
                            </Box>
                        }
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Default Timezone</InputLabel>
                        <Select
                            value={field.validation?.timezone || 'UTC'}
                            onChange={handleDefaultTimezoneChange}
                            label="Default Timezone"
                        >
                            {field.timeConfig?.availableTimezones.map((tz) => (
                                <MenuItem key={tz} value={tz}>
                                    {tz}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>

                <Grid item xs={12}>
                    <Divider sx={{ my: 1 }}>
                        <Chip label="Available Timezones" size="small" />
                    </Divider>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Configure which timezones users can select:
                    </Typography>

                    <Grid container spacing={2}>
                        {field.timeConfig?.availableTimezones.map((tz, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Box display="flex" alignItems="center" gap={1}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        value={tz}
                                        onChange={(e) => handleTimezoneUpdate(index, e.target.value)}
                                        placeholder="Timezone code"
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={() => handleDeleteTimezone(index)}
                                        color="error"
                                        disabled={field.timeConfig.availableTimezones.length <= 1}
                                    >
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}

                        <Grid item xs={12}>
                            <Button
                                startIcon={<AddIcon />}
                                onClick={handleAddTimezone}
                                variant="outlined"
                                size="small"
                                sx={{ mt: 1 }}
                            >
                                Add Timezone
                            </Button>
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12}>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                        <strong>Preview:</strong> Users will see Hour (1-12), Minute (00-59), AM/PM, and Timezone dropdowns
                    </Typography>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default TimeFieldConfig;